import { IValueInputAttachable } from "client/Forms/attachable";
import { IntegrationFlowClientCertificateAuthenticationSettingsFormData } from "../../common";
import { ReactElement, useCallback } from "react";
import { SecretValue } from "client/Api/Apps/common";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import {
	Button,
	FileSelector,
	FormItem,
	SecretValueInput,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { isNotNil } from "client/Misc/utils";
import styles from "./ClientCertificateSettings.module.less";
import {
	hasMaxSize,
	inline,
	isNotEmpty,
	isNotNullNorUndefined,
	isSupportedFileType
} from "client/Validation/rules";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";

export type ClientCertificateSettingsProps = {
	attachable: IValueInputAttachable<IntegrationFlowClientCertificateAuthenticationSettingsFormData>;
	isNew: boolean;
};

export const ClientCertificateSettings = (props: ClientCertificateSettingsProps): ReactElement => {
	const {
		attachable,
		isNew
	} = props;

	const { certificate, initialFileName } = attachable.value;

	const enforceToSupplyCertificatePassword = useCallback(
		(): void => {
			const passwordAttachable: IValueInputAttachable<SecretValue> = attachable.for("password");

			const { containsNewValue } = passwordAttachable.value;

			if (containsNewValue) {
				return;
			}

			passwordAttachable.reducer
				.setValue({
					containsNewValue: true,
					value: ""
				});
		},
		[attachable]
	);

	const enforceToSupplyCertificate = (): void => {
		const clientCertificateAttachable: IValueInputAttachable<OptionalModel<Base64FileUriUploadWithMetadataModel | null>> =
			attachable.for("certificate");

		const { isSet } = clientCertificateAttachable.value;
		if (isSet) {
			return;
		}

		clientCertificateAttachable.reducer
			.setValue({
				isSet: true,
				value: null
			});
	};

	return (
		<>
			<FormItem label={localize("_Certificate")} isRequired>
				<FileSelector
					{...attachable.for("certificate")
						.attachToFileSelector()
						.withOnChange(enforceToSupplyCertificatePassword)
					}
					contentTypes={SUPPORTED_CERTIFICATE_FILE_TYPES}
				>
					{onClick => (
						<div>
							<Button
								kind="secondary"
								text={localize("_SelectFile")}
								onClick={onClick}
								trackingId="add-certificate"
							/>
							<span className={styles.fileName}>
								{certificate.isSet === true
									? certificate.value?.filename
										?? ""
									: initialFileName
								}
							</span>
						</div>
					)}
				</FileSelector>

				<ValidationError
					{...attachable.for("certificate")
						.attachToValidationError()
					}
				/>

				{attachable.for("certificate")
					.whenType(
						isCertificateSet,
						(attachable) => {
							return (
								<>
									<ValidationError
										{...attachable.for("value")
											.attachToValidationError()
										}
									/>

									<ValidationError
										{...attachable.for("value")
											.for("filename")
											.attachToValidationError()
										}
									/>

									<ValidationError
										{...attachable.for("value")
											.for("uri")
											.attachToValidationError()
										}
									/>
								</>
							);
						}
					)
				}
			</FormItem>

			<FormItem label={localize("_CertificatePassword")} isRequired>
				<SecretValueInput
					{...attachable.for("password")
						.attachToSecretValueInput()
						.withOnChange(enforceToSupplyCertificate)
					}
					containsInitialValue={!isNew}
				/>
				<ValidationError {...attachable.for("password").for("value").attachToValidationError()} />
			</FormItem>
		</>
	);
};
ClientCertificateSettings.displayName = "ClientCertificateSettings";

const SUPPORTED_CERTIFICATE_FILE_TYPES: string[] = ["application/x-pkcs12"];
const CERTIFICATE_MAX_FILE_SIZE: number = 100 * 1024;

export const defaultClientCertificateSettings: IntegrationFlowClientCertificateAuthenticationSettingsFormData = {
	certificate: {
		isSet: false
	},
	password: {
		containsNewValue: true,
		value: ""
	},
	initialFileName: ""
};

export const clientCertificateValidationConfiguration: IValidationConfiguration<IntegrationFlowClientCertificateAuthenticationSettingsFormData> = createValidationConfiguration<IntegrationFlowClientCertificateAuthenticationSettingsFormData>();

clientCertificateValidationConfiguration.forScalar("certificate")
	.add(
		hasMaxSize(CERTIFICATE_MAX_FILE_SIZE),
		isSupportedFileType(SUPPORTED_CERTIFICATE_FILE_TYPES),
		inline(
			(file: OptionalModel<Base64FileUriUploadWithMetadataModel | null>): InputValidationResult => {
				if (file.isSet && file.value === null) {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_IsRequired")
					};
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
);

clientCertificateValidationConfiguration.forObject("password")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function isCertificateSet(
	clientCertificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>
): clientCertificate is SetOptionalModel<Base64FileUriUploadWithMetadataModel> {
	return clientCertificate.isSet === true
		&& isNotNil(clientCertificate.value);
}